import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import { QuickFindSpacesQuery } from './QuickFindSpacesQuery.graphql';
import { recentActivitiesQuery } from './recent-activities';
import { quickFindSearchQuery } from './search';
import { getUserAndTeamSearchResults } from './user-supplier';
import { parsePartialApolloResults } from './utils';
var INCLUDED_PRODUCTS = ['confluence', 'jira', 'townsquare'];

// To filter out Confluence comment results - although query does
// not include comment type it is still returning comment results
var INCLUDED_OBJECT_TYPES = ['page', 'blogpost', 'whiteboard', 'issue', 'project', 'goal', 'content'];
var PRE_QUERY_MAX_RECENT_CONTENTS_FETCHED = 200;
var PRE_QUERY_MAX_RECENT_SPACES_FETCHED = 10;
var PRE_QUERY_MAX_RECENT_CONTENTS_DISPLAYED = 7;
var PRE_QUERY_MAX_RECENT_SPACES_DISPLAYED = 3;
var POST_QUERY_MAX_CONTENTS = 7;
var POST_QUERY_MAX_SPACES = 1;
export var RECENT_ITEMS_SECTION_ID = 'recent-items';
export var RECENT_SPACES_SECTION_ID = 'recent-spaces';

// TODO: split pre- and post-query suppliers into seperate files, include 'pre-query' in the name
export var useQuickFindItemSupplier = function useQuickFindItemSupplier(_ref) {
  var confCloudClient = _ref.confCloudClient,
    aggAbsoluteUrl = _ref.aggAbsoluteUrl,
    markErrorAsHandled = _ref.markErrorAsHandled,
    itemRenderer = _ref.itemRenderer,
    recentContentResultMapper = _ref.recentContentResultMapper,
    recentSpaceResultMapper = _ref.recentSpaceResultMapper,
    userId = _ref.userId,
    cloudId = _ref.cloudId;
  return useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var getSections, recentActivitiesPromise, recentSpacesPromise, _yield$Promise$all$th, recentContents, recentSpaces;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          getSections = function getSections(_ref3) {
            var recentContents = _ref3.recentContents,
              recentSpaces = _ref3.recentSpaces;
            return {
              sections: [{
                id: RECENT_ITEMS_SECTION_ID,
                title: '',
                searchResults: recentContents,
                resultLimit: PRE_QUERY_MAX_RECENT_CONTENTS_DISPLAYED,
                resultRenderer: itemRenderer
              }, {
                id: RECENT_SPACES_SECTION_ID,
                title: '',
                searchResults: recentSpaces,
                resultLimit: PRE_QUERY_MAX_RECENT_SPACES_DISPLAYED,
                resultRenderer: itemRenderer
              }]
            };
          };
          if (userId) {
            _context.next = 3;
            break;
          }
          return _context.abrupt("return", getSections({
            recentContents: [],
            recentSpaces: []
          }));
        case 3:
          recentActivitiesPromise = recentActivitiesQuery({
            variables: {
              filteredProducts: INCLUDED_PRODUCTS,
              filteredObjectTypes: INCLUDED_OBJECT_TYPES,
              filteredRootContainerIds: ["ari:cloud:platform::site/".concat(cloudId), // covers Confluence, Jira
              "ari:cloud:townsquare::site/".concat(cloudId) // for some reason Atlas has a different root container id
              ],
              itemsLimit: PRE_QUERY_MAX_RECENT_CONTENTS_FETCHED
            },
            aggAbsoluteUrl: aggAbsoluteUrl
          });
          recentSpacesPromise = confCloudClient ? confCloudClient.query({
            query: QuickFindSpacesQuery,
            variables: {
              spaceLimit: PRE_QUERY_MAX_RECENT_SPACES_FETCHED
            }
          }) : Promise.resolve(undefined);
          _context.next = 7;
          return Promise.all([recentActivitiesPromise, recentSpacesPromise]).then(function (_ref4) {
            var _recentItemsResponse$, _recentSpacesResponse, _recentSpacesResults$;
            var _ref5 = _slicedToArray(_ref4, 2),
              recentItemsResponse = _ref5[0],
              recentSpacesResponse = _ref5[1];
            var recentContents = (recentItemsResponse === null || recentItemsResponse === void 0 || (_recentItemsResponse$ = recentItemsResponse.data) === null || _recentItemsResponse$ === void 0 || (_recentItemsResponse$ = _recentItemsResponse$.activity) === null || _recentItemsResponse$ === void 0 || (_recentItemsResponse$ = _recentItemsResponse$.myActivity) === null || _recentItemsResponse$ === void 0 ? void 0 : _recentItemsResponse$.all.edges.filter(function (edge) {
              var _edge$node;
              return edge === null || edge === void 0 || (_edge$node = edge.node) === null || _edge$node === void 0 || (_edge$node = _edge$node.object) === null || _edge$node === void 0 ? void 0 : _edge$node.data;
            }).map(recentContentResultMapper)) || [];
            var recentSpacesResults = parsePartialApolloResults(recentSpacesResponse === null || recentSpacesResponse === void 0 || (_recentSpacesResponse = recentSpacesResponse.data) === null || _recentSpacesResponse === void 0 ? void 0 : _recentSpacesResponse.myVisitedSpaces, recentSpacesResponse === null || recentSpacesResponse === void 0 ? void 0 : recentSpacesResponse.errors);
            var recentSpaces = (recentSpacesResults === null || recentSpacesResults === void 0 || (_recentSpacesResults$ = recentSpacesResults.nodes) === null || _recentSpacesResults$ === void 0 || (_recentSpacesResults$ = _recentSpacesResults$.spaces) === null || _recentSpacesResults$ === void 0 ? void 0 : _recentSpacesResults$.map(recentSpaceResultMapper)) || [];
            return {
              recentContents: recentContents,
              recentSpaces: recentSpaces
            };
          }).catch(function (err) {
            markErrorAsHandled && markErrorAsHandled(err);
            throw err;
          });
        case 7:
          _yield$Promise$all$th = _context.sent;
          recentContents = _yield$Promise$all$th.recentContents;
          recentSpaces = _yield$Promise$all$th.recentSpaces;
          return _context.abrupt("return", getSections({
            recentContents: recentContents,
            recentSpaces: recentSpaces
          }));
        case 11:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), [confCloudClient, userId, markErrorAsHandled, itemRenderer, recentContentResultMapper, recentSpaceResultMapper, aggAbsoluteUrl, cloudId]);
};
var CONFLUENCE_CONTENT_ENTITIES = ['ati:cloud:confluence:page', 'ati:cloud:confluence:blogpost', 'ati:cloud:confluence:attachment', 'ati:cloud:confluence:whiteboard', 'ati:cloud:confluence:database'];
var ATLAS_ENTITIES = ['ati:cloud:townsquare:project', 'ati:cloud:townsquare:goal', 'ati:cloud:townsquare:tag'];
var ATLAS_PROJECT_GOAL_TAG_SECTION_ID = 'townsquare.project,goal,tag';
export var CONFLUENCE_PAGE_BLOGPOST_ATTACHMENT_SECTION_ID = 'confluence.page,blogpost,attachment';
export var CONFLUENCE_SPACE_SECTION_ID = 'space';
export var PEOPLE_AND_TEAMS_SECTION_ID = 'people-and-teams';
var ProductKeys = {
  Confluence: 'confluence',
  Atlas: 'townsquare'
};
export var useQuickFindPostQueryItemSupplier = function useQuickFindPostQueryItemSupplier(_ref6) {
  var aggAbsoluteUrl = _ref6.aggAbsoluteUrl,
    cloudId = _ref6.cloudId,
    resultRenderer = _ref6.resultRenderer,
    confluenceResultMapper = _ref6.confluenceResultMapper,
    atlasResultMapper = _ref6.atlasResultMapper,
    spaceResultMapper = _ref6.spaceResultMapper,
    peopleAndTeamsResultMapper = _ref6.peopleAndTeamsResultMapper,
    sectionTitle = _ref6.sectionTitle,
    userId = _ref6.userId,
    orgId = _ref6.orgId,
    primaryProduct = _ref6.primaryProduct,
    experimentId = _ref6.experimentId,
    shadowExperimentId = _ref6.shadowExperimentId,
    experimentLayers = _ref6.experimentLayers;
  var intl = useIntl();
  return useCallback( /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(args) {
      var query, analyticsInfo, getPeopleAndTeamsResults, getConfluenceResults, getAtlasResults, getResults, _yield$getResults, contentResults, spaceResults, peopleAndTeamsResults, getContentSectionId;
      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) switch (_context5.prev = _context5.next) {
          case 0:
            query = args.query, analyticsInfo = args.analyticsInfo;
            getPeopleAndTeamsResults = /*#__PURE__*/function () {
              var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(query, userId, cloudId) {
                var peopleResults;
                return _regeneratorRuntime.wrap(function _callee2$(_context2) {
                  while (1) switch (_context2.prev = _context2.next) {
                    case 0:
                      if (!(!userId || !cloudId)) {
                        _context2.next = 2;
                        break;
                      }
                      return _context2.abrupt("return", []);
                    case 2:
                      _context2.next = 4;
                      return getUserAndTeamSearchResults({
                        query: query,
                        cloudId: cloudId,
                        principalId: userId || '',
                        orgId: orgId || '',
                        intl: intl
                      }).catch(function (e) {
                        console.error(e);
                        return [];
                      });
                    case 4:
                      peopleResults = _context2.sent;
                      return _context2.abrupt("return", peopleResults.map(peopleAndTeamsResultMapper));
                    case 6:
                    case "end":
                      return _context2.stop();
                  }
                }, _callee2);
              }));
              return function getPeopleAndTeamsResults(_x2, _x3, _x4) {
                return _ref8.apply(this, arguments);
              };
            }();
            getConfluenceResults = /*#__PURE__*/function () {
              var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(query, cloudId) {
                var _contentQueryResults$, _contentQueryResults$2;
                var contentQueryResults, contentResponse, contentResults, spaceResponse, spaceResults;
                return _regeneratorRuntime.wrap(function _callee3$(_context3) {
                  while (1) switch (_context3.prev = _context3.next) {
                    case 0:
                      _context3.next = 2;
                      return quickFindSearchQuery({
                        variables: {
                          query: query,
                          cloudIdARI: "ari:cloud:confluence::site/".concat(cloudId),
                          resultsLimit: POST_QUERY_MAX_CONTENTS,
                          spaceLimit: POST_QUERY_MAX_SPACES,
                          includeAncestors: true,
                          // to show breadcrumbs
                          contentEntities: CONFLUENCE_CONTENT_ENTITIES,
                          confluenceFilters: {
                            titleMatchOnly: true
                          },
                          searchSessionId: analyticsInfo === null || analyticsInfo === void 0 ? void 0 : analyticsInfo.searchSessionId,
                          queryVersion: analyticsInfo === null || analyticsInfo === void 0 ? void 0 : analyticsInfo.queryVersion,
                          includeSpaces: true,
                          experimentId: experimentId,
                          shadowExperimentId: shadowExperimentId,
                          experimentLayers: experimentLayers
                        },
                        aggAbsoluteUrl: aggAbsoluteUrl
                      });
                    case 2:
                      contentQueryResults = _context3.sent;
                      contentResponse = ((_contentQueryResults$ = contentQueryResults.data) === null || _contentQueryResults$ === void 0 || (_contentQueryResults$ = _contentQueryResults$.search.results) === null || _contentQueryResults$ === void 0 ? void 0 : _contentQueryResults$.edges) || [];
                      contentResults = contentResponse.map(confluenceResultMapper);
                      spaceResponse = ((_contentQueryResults$2 = contentQueryResults.data) === null || _contentQueryResults$2 === void 0 || (_contentQueryResults$2 = _contentQueryResults$2.search.spaces) === null || _contentQueryResults$2 === void 0 ? void 0 : _contentQueryResults$2.edges) || [];
                      spaceResults = spaceResponse.map(spaceResultMapper);
                      return _context3.abrupt("return", {
                        contentResults: contentResults,
                        spaceResults: spaceResults
                      });
                    case 8:
                    case "end":
                      return _context3.stop();
                  }
                }, _callee3);
              }));
              return function getConfluenceResults(_x5, _x6) {
                return _ref9.apply(this, arguments);
              };
            }();
            getAtlasResults = /*#__PURE__*/function () {
              var _ref10 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(query, analyticsInfo) {
                var _contentQueryResults$3;
                var contentQueryResults, contentResponse, contentResults;
                return _regeneratorRuntime.wrap(function _callee4$(_context4) {
                  while (1) switch (_context4.prev = _context4.next) {
                    case 0:
                      _context4.next = 2;
                      return quickFindSearchQuery({
                        variables: {
                          query: query,
                          cloudIdARI: "ari:cloud:confluence::site/".concat(cloudId),
                          contentEntities: ATLAS_ENTITIES,
                          confluenceFilters: {
                            titleMatchOnly: true
                          },
                          // this filter is applied on Atlas results as well
                          resultsLimit: POST_QUERY_MAX_CONTENTS,
                          spaceLimit: POST_QUERY_MAX_SPACES,
                          includeAncestors: false,
                          searchSessionId: analyticsInfo === null || analyticsInfo === void 0 ? void 0 : analyticsInfo.searchSessionId,
                          queryVersion: analyticsInfo === null || analyticsInfo === void 0 ? void 0 : analyticsInfo.queryVersion,
                          includeSpaces: false,
                          experimentId: experimentId,
                          shadowExperimentId: shadowExperimentId,
                          experimentLayers: experimentLayers
                        },
                        aggAbsoluteUrl: aggAbsoluteUrl
                      });
                    case 2:
                      contentQueryResults = _context4.sent;
                      contentResponse = ((_contentQueryResults$3 = contentQueryResults.data) === null || _contentQueryResults$3 === void 0 || (_contentQueryResults$3 = _contentQueryResults$3.search.results) === null || _contentQueryResults$3 === void 0 ? void 0 : _contentQueryResults$3.edges) || [];
                      contentResults = contentResponse.map(atlasResultMapper);
                      return _context4.abrupt("return", {
                        contentResults: contentResults,
                        spaceResults: []
                      });
                    case 6:
                    case "end":
                      return _context4.stop();
                  }
                }, _callee4);
              }));
              return function getAtlasResults(_x7, _x8) {
                return _ref10.apply(this, arguments);
              };
            }();
            getResults = function getResults(product) {
              switch (product) {
                case ProductKeys.Confluence:
                  return getConfluenceResults(query, cloudId);
                case ProductKeys.Atlas:
                  return getAtlasResults(query, analyticsInfo);
                default:
                  return getConfluenceResults(query, cloudId);
              }
            };
            _context5.next = 7;
            return getResults(primaryProduct);
          case 7:
            _yield$getResults = _context5.sent;
            contentResults = _yield$getResults.contentResults;
            spaceResults = _yield$getResults.spaceResults;
            _context5.next = 12;
            return getPeopleAndTeamsResults(query, userId, cloudId);
          case 12:
            peopleAndTeamsResults = _context5.sent;
            getContentSectionId = function getContentSectionId(product) {
              switch (product) {
                case ProductKeys.Confluence:
                  return CONFLUENCE_PAGE_BLOGPOST_ATTACHMENT_SECTION_ID;
                case ProductKeys.Atlas:
                  return ATLAS_PROJECT_GOAL_TAG_SECTION_ID;
                default:
                  return CONFLUENCE_PAGE_BLOGPOST_ATTACHMENT_SECTION_ID;
              }
            };
            return _context5.abrupt("return", {
              sections: [{
                id: getContentSectionId(primaryProduct),
                title: sectionTitle,
                searchResults: contentResults,
                resultRenderer: resultRenderer
              }, {
                id: CONFLUENCE_SPACE_SECTION_ID,
                searchResults: spaceResults,
                title: !contentResults.length ? sectionTitle : '',
                resultRenderer: resultRenderer
              }, {
                id: PEOPLE_AND_TEAMS_SECTION_ID,
                searchResults: peopleAndTeamsResults,
                title: !contentResults.length && !spaceResults.length ? sectionTitle : '',
                resultRenderer: resultRenderer
              }]
            });
          case 15:
          case "end":
            return _context5.stop();
        }
      }, _callee5);
    }));
    return function (_x) {
      return _ref7.apply(this, arguments);
    };
  }(), [cloudId, resultRenderer, confluenceResultMapper, atlasResultMapper, spaceResultMapper, peopleAndTeamsResultMapper, sectionTitle, intl, orgId, userId, aggAbsoluteUrl, primaryProduct, experimentId, shadowExperimentId, experimentLayers]);
};